'use client';

/**
 * Shared SMS OTP code-entry step.
 *
 * Rendered on the customer login (`/customer/login/[org-slug]`) and invitation
 * landing (`/r/[...slug]`) pages when a send returns `channel:'sms'`. The
 * customer types the 6-digit code they were texted back into the SAME browser
 * they started in; on success the session is minted server-side and we navigate
 * to the returned redirect. The email magic-link flow is unaffected.
 *
 * Mobile autofill: the input carries `autocomplete="one-time-code"` +
 * numeric hints so iOS QuickType offers the code, and (best-effort, HTTPS-only)
 * the WebOTP API fills it on Android/Chrome. Both degrade to manual entry.
 */

import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, MessageSquare, ShieldCheck } from 'lucide-react';
import TurnstileWidget from '@/components/auth/TurnstileWidget';
import { safeAssignLocation } from '@/lib/security/safeRedirect';

// The OTP length is fixed at 6 (see lib/auth/smsOtp). Declared locally so this
// client component doesn't pull the server-only crypto module into the bundle.
const CODE_LENGTH = 6;

// Minimal typings for the WebOTP API, which isn't in the standard DOM lib.
interface OtpCredentialRequestOptions extends CredentialRequestOptions {
  otp?: { transport: string[] };
}

export interface SmsCodeEntryProps {
  /** The identifier the customer submitted (a mobile number). */
  phone: string;
  organizationId: string;
  /** Internal path to land on once signed in. */
  redirectTo: string;
  brandName?: string;
  /** Optional styling to match the host page's primary button. */
  buttonClassName?: string;
  buttonStyle?: React.CSSProperties;
  /** Re-send a fresh code using a fresh Turnstile token. */
  onResend?: (captchaToken: string) => Promise<void>;
  /** Return to the identifier form ("use a different number"). */
  onUseDifferent?: () => void;
  /**
   * Navigate on success. Defaults to a full-page load so the new session
   * sticks. Callers that supply their own handler are responsible for
   * validating the URL — prefer the default, which routes through
   * `safeAssignLocation`.
   */
  onVerified?: (redirect: string) => void;
  /**
   * Route that verifies the code. Defaults to the SMS sign-in route; the
   * second factor of a multi-step sign-in uses `/api/auth/customer-challenge/verify`.
   */
  verifyEndpoint?: string;
  /** Shown instead of `phone` when the code went to a registered number. */
  displayPhone?: string;
  /**
   * Model 3 SMS-first (#652): the code was only the first factor and a magic
   * link has been sent to the registered email address.
   */
  onNextStep?: (next: 'email') => void;
  /** Direct Access account verification (#652): the code was issued under this purpose. */
  purpose?: 'account_verification';
}

export default function SmsCodeEntry(props: SmsCodeEntryProps) {
  const {
    phone, organizationId, redirectTo, brandName,
    buttonClassName, buttonStyle, onResend, onUseDifferent, onVerified,
    verifyEndpoint = '/api/auth/sms-otp/verify',
    displayPhone, onNextStep, purpose,
  } = props;

  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  // #885 — a failed or expired security check used to leave Verify disabled
  // with nothing on screen to say why.
  const [captchaFailed, setCaptchaFailed] = useState(false);

  const retryCaptcha = () => {
    setCaptchaFailed(false);
    setCaptchaToken(null);
    setTurnstileResetKey((k) => k + 1);
  };

  // Best-effort WebOTP autofill (Android/Chrome, HTTPS only). No-ops elsewhere.
  useEffect(() => {
    if (typeof window === 'undefined' || !('OTPCredential' in window)) return;
    const ac = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal } as OtpCredentialRequestOptions)
      .then((cred) => {
        const otp = (cred as { code?: string } | null)?.code;
        if (otp) setCode(otp.replace(/[^0-9]/g, '').slice(0, CODE_LENGTH));
      })
      .catch(() => { /* aborted or unsupported — manual entry still works */ });
    return () => ac.abort();
  }, []);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaToken || code.length !== CODE_LENGTH) return;
    setVerifying(true);
    setError('');
    setInfo('');
    try {
      const res = await fetch(verifyEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone, code, organizationId, redirectTo, captchaToken,
          ...(purpose ? { purpose } : {}),
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'That code could not be verified.');
      if (result.nextStep === 'email' && onNextStep) {
        setVerifying(false);
        onNextStep('email');
        return;
      }
      const redirect = (result.redirect as string) || redirectTo;
      if (onVerified) {
        onVerified(redirect);
      } else if (
        // The redirect is server-supplied, but it is still an attacker-
        // influenced value once it round-trips through the request body, so
        // it is validated before it reaches `window.location` (Aikido #380).
        // Post-sign-in landings are always internal paths, hence
        // `sameOriginOnly`.
        !safeAssignLocation(redirect, { sameOriginOnly: true })
      ) {
        throw new Error('That code could not be verified.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code could not be verified.');
      setCaptchaToken(null);
      setTurnstileResetKey((k) => k + 1);
      // Only released on failure. On success the navigation is already in
      // flight, and clearing it here would snap the button back to "Verify"
      // for the duration of the redirect.
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!onResend || !captchaToken || resending) return;
    setResending(true);
    setError('');
    setInfo('');
    try {
      await onResend(captchaToken);
      setInfo('We\u2019ve sent you a new code.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the code.');
    } finally {
      setCaptchaToken(null);
      setTurnstileResetKey((k) => k + 1);
      setResending(false);
    }
  };

  return (
    <div className="py-2" data-testid="sms-code-entry">
      <div className="mx-auto h-14 w-14 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
        <MessageSquare className="h-8 w-8 text-green-500" />
      </div>
      <h2 className="text-xl font-semibold text-center mb-1">Check your messages</h2>
      <p className="text-sm text-muted-foreground text-center mb-4">
        We&apos;ve sent a {CODE_LENGTH}-digit code to <strong>{displayPhone ?? phone}</strong>. Enter it below to sign in
        {brandName ? <> to {brandName}</> : null}.
      </p>

      <form onSubmit={handleVerify} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="sms-code">Verification code</Label>
          <Input
            id="sms-code"
            data-testid="sms-code-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, CODE_LENGTH))}
            placeholder="123456"
            required
            disabled={verifying}
            autoFocus
            className="h-11 text-center tracking-[0.5em] text-lg"
          />
        </div>

        {error && (
          <div role="alert" className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
            {error}
          </div>
        )}
        {info && !error && (
          <div role="status" className="text-sm text-muted-foreground bg-muted/40 p-3 rounded-md">
            {info}
          </div>
        )}

        <TurnstileWidget
          onSuccess={(token) => {
            setCaptchaFailed(false);
            setCaptchaToken(token);
          }}
          onError={() => {
            setCaptchaToken(null);
            setCaptchaFailed(true);
          }}
          onExpire={() => {
            setCaptchaToken(null);
            setCaptchaFailed(true);
          }}
          resetKey={turnstileResetKey}
          className="flex justify-center"
        />

        {!captchaToken && !verifying && captchaFailed && (
          <div
            role="alert"
            data-testid="sms-captcha-failed"
            className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-md text-center space-y-2"
          >
            <p>The security check did not complete. Please try it again to continue.</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="sms-captcha-retry"
              onClick={retryCaptcha}
            >
              Try the security check again
            </Button>
          </div>
        )}
        {!captchaToken && !verifying && !captchaFailed && code.length === CODE_LENGTH && (
          <p role="status" data-testid="sms-captcha-pending" className="text-sm text-muted-foreground text-center">
            Complete the security check above to continue.
          </p>
        )}

        <Button
          type="submit"
          data-testid="sms-verify-btn"
          className={buttonClassName ?? 'w-full h-11'}
          style={buttonStyle}
          disabled={verifying || !captchaToken || code.length !== CODE_LENGTH}
        >
          {verifying ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
          ) : (
            <><ShieldCheck className="mr-2 h-4 w-4" /> Verify &amp; sign in</>
          )}
        </Button>
      </form>

      <div className="mt-4 flex flex-col gap-2 text-center">
        {onResend && (
          <Button
            type="button"
            variant="ghost"
            data-testid="sms-resend-btn"
            onClick={handleResend}
            disabled={resending || !captchaToken}
            className="text-sm"
          >
            {resending ? 'Resending...' : 'Resend code'}
          </Button>
        )}
        {onUseDifferent && (
          <button
            type="button"
            onClick={onUseDifferent}
            className="text-xs text-muted-foreground underline underline-offset-2"
          >
            Use a different number
          </button>
        )}
      </div>
    </div>
  );
}
